import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import SectionSetting from "components/common/SectionSetting";
import {
  getAssetEventTimeline,
  getAssetPlannings,
} from "service/events";

// ---------------------------------------------------------------------------
// AV-D6 — Planning Gantt (NEW view; does NOT modify the Planning table).
//
// Consumes the `gantt` block of the unified BE aggregation endpoint:
//   GET /api/assets/<asset_id>/events/timeline  ->  { timeline, gantt }
// gantt item shape (see common/events.py _gantt_bar_from_planning):
//   { id, well_name, uwi, event_type, priority, expected_uplift, cost,
//     currency, resource, bar:{ start, end, duration, duration_unit } }
// One horizontal bar per planning item, positioned by bar.start/bar.end on a
// real time axis, labelled with resource + priority. Drawn with an ECharts
// `custom` series so each bar is an explicit rect on a time x-axis (correct
// time scale, PE 验收: 甘特时间轴正确). If the endpoint isn't deployed yet it
// degrades to deriving bars from the existing planning endpoint (start_date +
// duration days), or an empty-state when nothing resolves.
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

// Priority -> bar colour. Tolerant of numeric (1..n) or label priorities.
const PRIORITY_COLORS = {
  critical: "#FF5454",
  high: "#FF8C00",
  medium: "#FFB800",
  low: "#2E9E5B",
};

function priorityColor(priority) {
  if (priority == null) return "#4169E1";
  const key = String(priority).toLowerCase();
  if (PRIORITY_COLORS[key]) return PRIORITY_COLORS[key];
  // Numeric priority: 1 = highest.
  const n = Number(priority);
  if (!Number.isNaN(n)) {
    if (n <= 1) return "#FF5454";
    if (n === 2) return "#FF8C00";
    if (n === 3) return "#FFB800";
    return "#2E9E5B";
  }
  return "#4169E1";
}

function toMillis(value) {
  if (value == null) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

// Normalise into a gantt bar. Handles BOTH shapes:
//   • BE gantt item: { ..., bar:{ start, end, duration, duration_unit } }
//   • raw planning row (fallback): { start_date, end_date, duration, ... }
// Resolves the bar end from bar.end / end_date, else start + duration(days).
function toBar(item) {
  const b = item.bar || {};
  const start = toMillis(b.start || item.start_date || item.scheduled_date || item.date);
  if (start == null) return null;
  let end = toMillis(b.end || item.end_date || item.completed_date);
  if (end == null) {
    // bar.duration is already normalised to days by the BE; raw rows carry
    // `duration` in `duration_unit` (day|hour).
    let days = Number(b.duration);
    if (Number.isNaN(days)) {
      const raw = Number(item.duration);
      const unit = String(item.duration_unit || "day").toLowerCase();
      days = Number.isNaN(raw) ? 1 : unit === "hour" ? raw / 24 : raw;
    }
    end = start + Math.max(days, 0.25) * DAY_MS;
  }
  if (end < start) end = start + DAY_MS;
  const priority = item.priority != null ? item.priority : item.severity;
  return {
    label: item.well_name || item.event || item.event_type || "Plan",
    detail: item.event_type || item.event || "",
    resource: item.resource || item.rig || item.crew || "",
    priority,
    expected_uplift: item.expected_uplift,
    cost: item.cost,
    currency: item.currency,
    status: item.status || "",
    start,
    end,
    color: priorityColor(priority),
  };
}

function PlanningGantt(props) {
  const { assetId, loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];
  const [bars, setBars] = useState(null); // null = loading, [] = empty
  const [degraded, setDegraded] = useState(false);

  useEffect(() => {
    if (assetId == null) {
      setBars(null);
      return;
    }
    let cancelled = false;
    setBars(null);
    setDegraded(false);
    showLoadingIcon && showLoadingIcon();

    const finish = (rows, isDegraded) => {
      if (cancelled) return;
      const mapped = (Array.isArray(rows) ? rows : [])
        .map(toBar)
        .filter(Boolean);
      setBars(mapped);
      setDegraded(!!isDegraded);
      hideLoadingIcon && hideLoadingIcon();
    };

    // Primary: the `gantt` block of the unified aggregation endpoint.
    getAssetEventTimeline(
      assetId,
      (data) => {
        const arr =
          data && Array.isArray(data.gantt)
            ? data.gantt
            : Array.isArray(data)
            ? data
            : [];
        finish(arr, false);
      },
      () => {
        // §3 graceful degradation: derive bars from the existing planning rows.
        if (cancelled) return;
        getAssetPlannings(
          assetId,
          (rows) => finish(rows, true),
          () => finish([], true)
        );
      }
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  const option = useMemo(() => {
    if (!bars || bars.length === 0) return null;
    // Sort by start so the earliest plan sits at the top row.
    const sorted = bars
      .slice()
      .sort((a, b) => a.start - b.start || a.end - b.end);
    const categories = sorted.map(
      (b, i) => `${i + 1}. ${b.label}${b.detail ? " · " + b.detail : ""}`
    );
    // Each custom datum: [categoryIndex, start, end, barMeta].
    const data = sorted.map((b, i) => ({
      value: [i, b.start, b.end],
      itemStyle: { color: b.color },
      bar: b,
    }));

    const renderItem = (params, api) => {
      const categoryIndex = api.value(0);
      const start = api.coord([api.value(1), categoryIndex]);
      const end = api.coord([api.value(2), categoryIndex]);
      const height = api.size([0, 1])[1] * 0.6;
      const x = start[0];
      const width = Math.max(end[0] - start[0], 2); // min 2px so 0-day plans show
      const y = start[1] - height / 2;
      const rectShape = {
        x,
        y,
        width,
        height,
      };
      return {
        type: "rect",
        shape: rectShape,
        style: api.style(),
      };
    };

    return {
      tooltip: {
        formatter: (p) => {
          const b = p.data && p.data.bar;
          if (!b) return "";
          const fmt = (ms) => new Date(ms).toISOString().slice(0, 10);
          const lines = [
            `<b>${b.label}</b>`,
            b.detail ? b.detail : null,
            `${fmt(b.start)} → ${fmt(b.end)}`,
            b.resource ? `Resource: ${b.resource}` : null,
            b.priority != null ? `Priority: ${b.priority}` : null,
            b.expected_uplift != null
              ? `Exp. uplift: ${b.expected_uplift} bbl/d`
              : null,
            b.cost != null
              ? `Cost: ${b.cost}${b.currency ? " " + b.currency : ""}`
              : null,
            b.status ? `Status: ${b.status}` : null,
          ].filter(Boolean);
          return lines.join("<br/>");
        },
      },
      grid: { left: 8, right: 24, top: 16, bottom: 28, containLabel: true },
      xAxis: {
        type: "time",
        axisLabel: { color: "#cbd2e0" },
        splitLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
      },
      yAxis: {
        type: "category",
        data: categories,
        inverse: true,
        axisLabel: { color: "#cbd2e0", fontSize: 11 },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      series: [
        {
          type: "custom",
          renderItem,
          encode: { x: [1, 2], y: 0 },
          label: {
            show: true,
            position: "insideLeft",
            color: "#fff",
            fontSize: 10,
            formatter: (p) => {
              const b = p.data && p.data.bar;
              if (!b) return "";
              const tags = [];
              if (b.resource) tags.push(b.resource);
              if (b.priority != null) tags.push("P" + b.priority);
              return tags.join(" · ");
            },
          },
          data,
        },
      ],
    };
  }, [bars]);

  return (
    <div className="cards-container h-100">
      <div className="cards-header d-flex flex-row justify-content-between align-items-center">
        <span className="header-title">
          Planning Gantt
          {degraded && (
            <span
              className="badge ms-2"
              style={{ background: "rgba(255,184,0,0.15)", color: "#FFB800" }}
              title="Gantt endpoint unavailable — bars derived from planning rows (start + duration)."
            >
              derived
            </span>
          )}
        </span>
        <div className="header-menu-container d-flex flex-row align-items-center">
          <div className="cards-setting-btn">
            <SectionSetting disable />
          </div>
        </div>
      </div>
      <div className="cards-content">
        {bars == null ? (
          <div className="text-muted text-center p-4">Loading gantt…</div>
        ) : bars.length === 0 ? (
          <div className="text-muted text-center p-4">
            <i
              className="bi bi-bar-chart-steps d-block mb-2"
              style={{ fontSize: 24 }}
            />
            No planning items to schedule for this asset.
          </div>
        ) : (
          <ReactECharts
            option={option}
            style={{ height: Math.max(180, bars.length * 34 + 60) }}
          />
        )}
      </div>
    </div>
  );
}

export { PlanningGantt };
export default PlanningGantt;
